import { LogLevel } from '@/lib/logger/logger.constants'
import type { Transport } from '@/lib/logger/logger.types'
import * as Sentry from '@sentry/react-native'
import { prepareMetadata } from './utils'

const SENTRY_THROTTLE_TIMEOUT = 5000

let sentrySendTimeout: ReturnType<typeof setTimeout> | null = null

const queuedSentryMessages: [string, Parameters<typeof Sentry.captureMessage>[1]][] = []

const sendQueuedMessages = () => {
	while (queuedSentryMessages.length > 0) {
		const message = queuedSentryMessages.shift()
		if (message) {
			Sentry.captureMessage(message[0], message[1])
		}
	}
}

const queueMessageForSentry = (
	message: string,
	captureContext: Parameters<typeof Sentry.captureMessage>[1]
) => {
	queuedSentryMessages.push([message, captureContext])

	if (!sentrySendTimeout) {
		sentrySendTimeout = setTimeout(() => {
			sentrySendTimeout = null
			sendQueuedMessages()
		}, SENTRY_THROTTLE_TIMEOUT)
	}
}

export const sentryTransport: Transport = (
	level,
	message,
	{ type, tags, ...metadata },
	timestamp
) => {
	const meta = prepareMetadata(metadata)

	/**
	 * If a string, report a breadcrumb
	 */
	if (typeof message === 'string') {
		const severity = (
			{
				[LogLevel.Debug]: 'debug',
				[LogLevel.Info]: 'info',
				[LogLevel.Log]: 'log', // Sentry value here is undefined
				[LogLevel.Warn]: 'warning',
				[LogLevel.Error]: 'error',
			} as const
		)[level]

		Sentry.addBreadcrumb({
			message,
			data: meta,
			type: type || 'default',
			level: severity,
			timestamp: timestamp / 1000, // Sentry expects seconds
		})

		/**
		 * Send all higher levels with `captureMessage`, with appropriate severity
		 * level
		 */
		if (level === 'error' || level === 'warn' || level === 'log') {
			const messageLevel =
				(({
					[LogLevel.Log]: 'log',
					[LogLevel.Warn]: 'warning',
					[LogLevel.Error]: 'error',
				}[level] || 'log') as Sentry.Breadcrumb['level']) ?? 'log'

			// Defer non-critical messages so they're sent in a batch
			queueMessageForSentry(message, {
				level: messageLevel,
				tags: tags ?? {},
				extra: meta,
			})
		}
	} else {
		Sentry.captureException(message, {
			tags: tags ?? {},
			extra: meta,
		})
	}
}
